/**
 * Storyblok Visual Editor Detection and Bridge Utilities
 *
 * Detects when the app is running inside Storyblok's visual editor iframe
 * and provides utilities to interact with the Storyblok Bridge API.
 */

import StoryblokBridge from '@storyblok/preview-bridge';

/**
 * Valid Storyblok domains that are allowed to embed the app
 */
const ALLOWED_STORYBLOK_DOMAINS = [
  'app.storyblok.com',
  'storyblok.com',
  'app.storyblok.dev', // For development/staging
];

/**
 * Check if the current request is from a Storyblok domain
 * @returns {boolean} True if referrer is from Storyblok domain
 */
function isFromStoryblokDomain() {
  try {
    if (typeof window === 'undefined' || !document.referrer) {
      return false;
    }

    const referrerUrl = new URL(document.referrer);
    const referrerHost = referrerUrl.hostname.toLowerCase();

    return ALLOWED_STORYBLOK_DOMAINS.some(
      domain => referrerHost === domain || referrerHost.endsWith(`.${domain}`),
    );
  } catch (error) {
    console.warn('[Storyblok] Error checking referrer domain:', error);
    return false;
  }
}

/**
 * Check if the app is running inside an iframe
 * @returns {boolean} True if running in iframe
 */
function isInIframe() {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.self !== window.top;
  } catch (error) {
    // If we can't access window.top, we're likely in an iframe with cross-origin restrictions
    return true;
  }
}

// Store bridge instance
let storyblokBridgeInstance = null;

/**
 * Check if Storyblok Bridge is available
 * @returns {boolean} True if bridge instance exists
 */
function hasStoryblokBridge() {
  return storyblokBridgeInstance !== null;
}

/**
 * Check if URL contains Storyblok preview/visual editor parameters
 * @returns {boolean} True if Storyblok parameters are present
 */
function hasStoryblokUrlParams() {
  try {
    if (typeof window === 'undefined' || !window.location) {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    const storyblokParams = [
      '_storyblok_tk',
      '_storyblok',
      'preview_token',
      'previewToken',
      '_storyblok_preview_tk',
    ];

    // Also check for _storyblok_tk[token] or _storyblok_tk[space_id] (bracket notation)
    const hasStoryblokTk = Array.from(params.keys()).some(
      key => key.startsWith('_storyblok_tk') || key.includes('_storyblok'),
    );

    return storyblokParams.some(param => params.has(param)) || hasStoryblokTk;
  } catch (error) {
    return false;
  }
}

/**
 * Extract Storyblok story ID from URL parameters
 * According to Storyblok docs, the story ID is in the _storyblok parameter
 * @returns {string|number|null} Story ID or null if not found
 */
export function getStoryblokStoryIdFromUrl() {
  try {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    const storyId = params.get('_storyblok');

    if (storyId) {
      // Convert to number if it's numeric, otherwise return as string
      const numericId = Number(storyId);
      return isNaN(numericId) ? storyId : numericId;
    }

    return null;
  } catch (error) {
    console.warn('[Storyblok] Error extracting story ID from URL:', error);
    return null;
  }
}

/**
 * Extract Storyblok preview token from URL parameters
 * Handles both standard params and bracket notation like _storyblok_tk[token]
 * @returns {string|null} Preview token or null if not found
 */
export function getStoryblokTokenFromUrl() {
  try {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }

    const params = new URLSearchParams(window.location.search);

    // Try bracket notation first: _storyblok_tk[token]
    const bracketToken = params.get('_storyblok_tk[token]');
    if (bracketToken) {
      return bracketToken;
    }

    // Try standard parameters
    const standardToken =
      params.get('_storyblok_tk') ||
      params.get('_storyblok_preview_tk') ||
      params.get('preview_token') ||
      params.get('previewToken');

    return standardToken || null;
  } catch (error) {
    console.warn('[Storyblok] Error extracting token from URL:', error);
    return null;
  }
}

/**
 * Get Storyblok space ID from URL parameters
 * @returns {string|null} Space ID or null if not found
 */
export function getStoryblokSpaceIdFromUrl() {
  try {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    const spaceId = params.get('_storyblok_tk[space_id]');
    return spaceId || null;
  } catch (error) {
    console.warn('[Storyblok] Error extracting space ID from URL:', error);
    return null;
  }
}

/**
 * Check if the app is running in Storyblok Visual Editor
 *
 * This function checks multiple conditions:
 * 1. Storyblok Bridge API is available (window.storyblok)
 * 2. App is running in an iframe
 * 3. Referrer is from Storyblok domain (security check)
 * 4. URL contains Storyblok parameters (optional but helpful)
 *
 * @returns {boolean} True if app is in Storyblok visual editor
 */
export function isInStoryblokVisualEditor() {
  try {
    // Check if Storyblok Bridge is available (may be false initially before loading)
    const hasBridge = hasStoryblokBridge();

    // Check if we're in an iframe
    const inIframe = isInIframe();

    // Check referrer domain for security
    const fromStoryblok = isFromStoryblokDomain();

    // Check URL parameters
    const hasParams = hasStoryblokUrlParams();

    // Check for Storyblok space ID in URL (strong indicator)
    const hasSpaceId = getStoryblokSpaceIdFromUrl() !== null;

    // Visual editor is active if:
    // - We're in iframe AND referrer is from Storyblok (most reliable)
    // OR
    // - We have Storyblok URL params (for preview links)
    // OR
    // - We have Storyblok space_id in URL (strong indicator of visual editor)
    // Note: Bridge doesn't need to be loaded yet - we detect first, then load bridge
    const isVisualEditor = (inIframe && fromStoryblok) || hasParams || hasSpaceId;

    if (isVisualEditor) {
      console.log('═══════════════════════════════════════════════════════');
      console.log('🎨 [Storyblok] VISUAL EDITOR DETECTED');
      console.log('═══════════════════════════════════════════════════════');
      console.log('🔍 Detection Details:');
      console.log(
        '   ✓ Bridge Available:',
        hasBridge,
        hasBridge ? '(already loaded)' : '(will load now)',
      );
      console.log('   ✓ In Iframe:', inIframe);
      console.log('   ✓ From Storyblok Domain:', fromStoryblok);
      console.log('   ✓ Has URL Params:', hasParams);
      console.log('   ✓ Has Space ID:', hasSpaceId);
      console.log('⏰ Timestamp:', new Date().toISOString());
      console.log('═══════════════════════════════════════════════════════');
      console.log('💡 Bridge will be loaded and initialized next...');
      console.log('═══════════════════════════════════════════════════════');
    }

    return isVisualEditor;
  } catch (error) {
    console.warn('[Storyblok] Error detecting visual editor:', error);
    return false;
  }
}

/**
 * Load Storyblok Bridge
 * Uses the official @storyblok/preview-bridge package
 * @returns {Promise<void>} Promise that resolves when bridge is initialized
 */
export function loadStoryblokBridge() {
  return new Promise((resolve, reject) => {
    console.log('🔍 [Storyblok] loadStoryblokBridge() called');

    // Check if bridge is already initialized
    if (hasStoryblokBridge()) {
      console.log('✅ [Storyblok] Bridge already initialized, skipping');
      resolve();
      return;
    }

    try {
      console.log('📦 [Storyblok] Creating new StoryblokBridge instance...');
      console.log('📦 [Storyblok] StoryblokBridge import:', typeof StoryblokBridge);

      // Initialize the official Storyblok Bridge
      // According to docs: https://www.storyblok.com/docs/packages/storyblok-preview-bridge
      storyblokBridgeInstance = new StoryblokBridge({
        preventClicks: true, // Prevent link clicks in visual editor
        // Add resolveRelations and resolveLinks if needed
        // resolveRelations: ['article.author'],
        // resolveLinks: 'url',
      });

      console.log('═══════════════════════════════════════════════════════');
      console.log('✅ [Storyblok] Bridge Created Successfully');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📦 Using: @storyblok/preview-bridge');
      console.log('⚙️  Options:', {
        preventClicks: true,
      });
      console.log('📦 Bridge Instance:', storyblokBridgeInstance);
      console.log('🔍 Bridge type:', typeof storyblokBridgeInstance);
      console.log('🔍 Has on method:', typeof storyblokBridgeInstance?.on === 'function');
      console.log('⏰ Timestamp:', new Date().toISOString());
      console.log('═══════════════════════════════════════════════════════');
      resolve();
    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ [Storyblok] Failed to initialize bridge');
      console.error('═══════════════════════════════════════════════════════');
      console.error('Error:', error);
      console.error('Error message:', error?.message);
      console.error('Error stack:', error?.stack);
      console.error('StoryblokBridge available:', typeof StoryblokBridge);
      console.error('═══════════════════════════════════════════════════════');
      reject(new Error('Failed to initialize Storyblok bridge: ' + error.message));
    }
  });
}

/**
 * Initialize Storyblok Bridge if available
 * Sets up event listeners for visual editor interactions
 * @param {string|number} storyId - Optional story ID (not used with official bridge, kept for compatibility)
 * @returns {Object|null} Storyblok bridge instance or null
 */
export function initStoryblokBridge(storyId = null) {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    // Load bridge if not already loaded
    if (!hasStoryblokBridge()) {
      loadStoryblokBridge()
        .then(() => {
          // Retry initialization after bridge loads
          setTimeout(() => {
            initStoryblokBridge(storyId);
          }, 100);
        })
        .catch(error => {
          console.warn('[Storyblok] Failed to load bridge:', error);
        });
      return null;
    }

    const bridge = storyblokBridgeInstance;

    if (!bridge) {
      return null;
    }

    // Set up event listeners for visual editor interactions
    // According to Storyblok docs: https://www.storyblok.com/docs/packages/storyblok-preview-bridge
    // Events: 'input', 'change', 'published', 'enterEditMode'

    // Verify bridge.on method exists
    if (typeof bridge.on !== 'function') {
      console.error('[Storyblok] Bridge.on is not a function. Bridge object:', bridge);
      return null;
    }

    console.log('[Storyblok] Setting up event listeners...');

    // Listen for input events (live editing)
    try {
      bridge.on('input', ({ story }) => {
        console.log('═══════════════════════════════════════════════════════');
        console.log('🔔 [Storyblok Bridge] INPUT EVENT DETECTED');
        console.log('═══════════════════════════════════════════════════════');
        console.log('📝 Story data:', story);
        console.log('⏰ Timestamp:', new Date().toISOString());
        console.log('═══════════════════════════════════════════════════════');
        // The bridge handles live editing automatically
        // You can add custom logic here if needed to update your app state
      });
      console.log('[Storyblok] ✓ Input event listener attached');
    } catch (error) {
      console.error('[Storyblok] Error attaching input listener:', error);
    }

    // Listen for change and published events (reload page)
    try {
      bridge.on(['change', 'published'], payload => {
        console.log('═══════════════════════════════════════════════════════');
        console.log(`🔔 [Storyblok Bridge] ${payload.action?.toUpperCase()} EVENT DETECTED`);
        console.log('═══════════════════════════════════════════════════════');
        console.log('📦 Payload:', payload);
        console.log('📄 Story ID:', payload.storyId);
        console.log('🔗 Slug:', payload.slug);
        console.log('🔄 Slug Changed:', payload.slugChanged);
        console.log('⏰ Timestamp:', new Date().toISOString());
        console.log('═══════════════════════════════════════════════════════');
        console.log('🔄 Reloading page in 100ms...');

        // Reload page on save/publish events to ensure all content is updated
        // Small delay to ensure content is saved
        setTimeout(() => {
          console.log('🔄 Page reload triggered');
          window.location.reload();
        }, 100);
      });
      console.log('[Storyblok] ✓ Change/Published event listeners attached');
    } catch (error) {
      console.error('[Storyblok] Error attaching change/published listeners:', error);
    }

    // Listen for enterEditMode event
    try {
      bridge.on('enterEditMode', payload => {
        console.log('═══════════════════════════════════════════════════════');
        console.log('🎨 [Storyblok Bridge] ENTER EDIT MODE DETECTED');
        console.log('═══════════════════════════════════════════════════════');
        console.log('📦 Payload:', payload);
        console.log('📱 App Version:', payload.appVersion);
        console.log('🆔 Story ID:', payload.storyId);
        console.log('🧩 Block ID:', payload.blockId);
        console.log('📋 Component Names:', payload.componentNames);
        console.log('⏰ Timestamp:', new Date().toISOString());
        console.log('═══════════════════════════════════════════════════════');
      });
      console.log('[Storyblok] ✓ EnterEditMode event listener attached');
    } catch (error) {
      console.error('[Storyblok] Error attaching enterEditMode listener:', error);
    }

    // Use pingEditor to verify we're in the editor
    bridge.pingEditor(payload => {
      console.log('═══════════════════════════════════════════════════════');
      console.log('📡 [Storyblok Bridge] PING EDITOR RESPONSE');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📦 Payload:', payload);
      if (bridge.isInEditor()) {
        console.log('✅ Confirmed: Running in Storyblok Visual Editor');
        console.log('🎯 Bridge is active and ready to receive events');
      } else {
        console.log('⚠️  Not in editor mode (may be preview mode)');
      }
      console.log('⏰ Timestamp:', new Date().toISOString());
      console.log('═══════════════════════════════════════════════════════');
    });

    console.log('═══════════════════════════════════════════════════════');
    console.log('✅ [Storyblok] Bridge initialized successfully');
    console.log('═══════════════════════════════════════════════════════');
    console.log('📡 All event listeners attached');
    console.log('🎯 Bridge instance:', bridge);
    console.log('⏰ Timestamp:', new Date().toISOString());
    console.log('═══════════════════════════════════════════════════════');
    console.log('💡 Watch for these events:');
    console.log('   - 🔔 INPUT: Field value changed (live editing)');
    console.log('   - 🔔 CHANGE: Story saved');
    console.log('   - 🔔 PUBLISHED: Story published');
    console.log('   - 🔔 ENTEREDITMODE: Editor initialized');
    console.log('═══════════════════════════════════════════════════════');
    return bridge;
  } catch (error) {
    console.warn('[Storyblok] Error initializing bridge:', error);
    return null;
  }
}

/**
 * Get Storyblok Bridge instance if available
 * @returns {Object|null} Storyblok bridge instance or null
 */
export function getStoryblokBridge() {
  try {
    if (hasStoryblokBridge()) {
      return storyblokBridgeInstance;
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Check if we should bypass authentication
 * This is a convenience function that combines visual editor detection
 * with preview mode detection from storyblok.js
 * @returns {boolean} True if authentication should be bypassed
 */
export function shouldBypassAuth() {
  return isInStoryblokVisualEditor();
}
